import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { withRouter } from 'react-router';
import { Redirect } from 'react-router-dom';

import { hasRoleAnonymous } from 'common/userRoles';
import { getTourToReschedule } from 'common/tourHelpers';
import { SR_API_ACTION_GET_LISTING_LIST } from 'api/actionTypes';
import { SCREEN_ID_LISTINGS } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';

import Navbar from 'client/components/Navbar';
import NoResults from 'client/components/NoResults';
import TabBar from 'client/components/TabBar';
import NavbarButton from 'client/components/NavbarButton';
import ListingsListView from 'client/components/ListingsListView';
import SrResultsMatchedCard from 'client/components/SrResultsMatchedCard';
import SrContinueApplicationButton from 'client/components/SrContinueApplicationButton';
import SrSuggestedSearchesSection from 'client/components/SrSuggestedSearchesSection';
import SrSuggestedSearchRemove from 'client/components/SrSuggestedSearchRemove';

import ListingActions from 'client/actions/Listing';
import HouseholdActions from 'client/actions/Household';
import UserActions from 'client/actions/User';
import SearchActions from 'client/actions/Search';

import branch from 'client/core/branch';

@withTransitionHook
@withScreenId(SCREEN_ID_LISTINGS)
@withScrollToTop
@withApiState({
  listingsApiState: {
    action: SR_API_ACTION_GET_LISTING_LIST,
  },
})
@branch({
  currentUser: ['currentUser'],
  householdApplications: ['householdApplications'],
  listingsTotal: ['listings', 'total'],
  listings: ['listings', 'records'],
  listingsIsFullyLoaded: ['listings', 'isFullyLoaded'],
  listingsViewMoreCount: ['listings', 'viewMoreCount'],
  listingsViewMoreIndex: ['listings', 'viewMoreIndex'],
  totalListingsCount: ['listings', 'totalListingsCount'],
  bookmarksByListingId: ['bookmarksByListingId'],
  listingsViewMode: ['listingsViewMode'],
  educationTourCardClosed: [
    'currentUser',
    'settings',
    'educationTourCardClosed',
  ],
  tours: ['tours'],
  listingPhotoResolution: ['listingPhotoResolution'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  suggestedSearchFilters: ['search', 'suggestedSearchFilters'],
  selectedSuggestedSearchFilter: ['search', 'selectedSuggestedSearchFilter'],
})
@withRouter
class ListingsPage extends Component {
  static fetchData(match, inject) {
    const currentUser = inject.state.get(['currentUser']);
    const isAnonymous = hasRoleAnonymous(currentUser.roles);
    const fetchDataPromises = [
      ListingActions.getMoreListingsCount(inject),
      ListingActions.getTotalListingsCount(inject),
    ];

    if (inject.state.get(['listings', 'records']).length === 0) {
      return Promise.all([
        ...fetchDataPromises,
        ListingActions.getList(inject),
        UserActions.getSuggestedSearchFilters(inject),
        !isAnonymous && HouseholdActions.loadRenterProfile(inject, { userId: currentUser.id }),
      ]);
    }
    if (inject.state.get(['listings', 'source']) === 'map') {
      return Promise.all(fetchDataPromises);
    }
  }

  static getPageMetadata = () => ({
    pageTitle: 'Rent the best homes in Texas | Sunroom Rentals',
    section: 'Listings',
    keywords: 'rent, home, austin, tour, apartment',
    description: (
      'Spend less time scheduling tours, our rental experts will schedule ' +
      'and confirm tours for you.'
    ),
    image: 'https://sunroomrentals.com/images/metadata/og-house.jpg',
  })

  static propTypes = {
    householdApplications: PropTypes.array,
    listingsApiState: PropTypes.object.isRequired,
    listingsIsFullyLoaded: PropTypes.bool.isRequired,
    listingsTotal: PropTypes.number.isRequired,
    listings: PropTypes.array.isRequired,
    listingsViewMoreCount: PropTypes.number,
    listingsViewMoreIndex: PropTypes.number,
    educationTourCardClosed: PropTypes.bool,
    bookmarksByListingId: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    listingsViewMode: PropTypes.string.isRequired,
    tours: PropTypes.array,
    listingPhotoResolution: PropTypes.number.isRequired,
    suggestedSearchFilters: PropTypes.array,
    selectedSuggestedSearchFilter: PropTypes.object,
  }

  constructor(props) {
    super(props);

    const locationQuery = queryString.parse(props.location.search);

    this.state = {
      appstoreRedirect: locationQuery.appstore || false,
      playstoreRedirect: locationQuery.playstore || false,
    };

    this.lastUpdate = Date.now();
  }

  componentDidMount() {
    const { listingsIsFullyLoaded, listings } = this.props;
    if (listingsIsFullyLoaded && listings.length === 0) {
      this._enableAllListings();
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { listingsIsFullyLoaded } = this.props;
    const shouldEnableAllListings = (
      !listingsIsFullyLoaded
      && nextProps.listingsIsFullyLoaded
      && !nextProps.listings.length
      && nextProps.listingsViewMoreCount
    );
    if (shouldEnableAllListings) {
      this._enableAllListings();
    }
  }

  _onLoadMore = () => {
    const { listingsIsFullyLoaded, dispatch } = this.props;
    if (!listingsIsFullyLoaded) {
      dispatch(ListingActions.getList);
    }
  }

  _enableAllListings = () => {
    this.props.dispatch(ListingActions.enableAllListings);
  }

  _removeSuggestedFilter = () => {
    this.props.dispatch(SearchActions.removeSuggestedFilter);
  }

  render() {
    const {
      householdApplications,
      listingsApiState,
      listingsIsFullyLoaded,
      listings,
      listingsTotal,
      listingsViewMoreCount,
      listingsViewMoreIndex,
      totalListingsCount,
      educationTourCardClosed,
      bookmarksByListingId,
      tours,
      currentUser,
      history,
      listingsViewMode,
      listingPhotoResolution,
      renterProfilesByUserId,
      requestedFrom,
      suggestedSearchFilters,
      selectedSuggestedSearchFilter,
      dispatch,
    } = this.props;

    const {
      appstoreRedirect,
      playstoreRedirect,
    } = this.state;

    if (appstoreRedirect) {
      global.location = 'https://itunes.apple.com/us/app/sunroom-rentals/id1445221096?mt=8';
    }

    if (playstoreRedirect) {
      global.location = 'https://play.google.com/store/apps/details?id=com.mobile.sunroomrentals';
    }

    if (listingsViewMode === 'map') {
      return <Redirect to='/explore/map' />;
    }

    // Redirect to the Tour page if the user has a tour with a past or null startDate,
    // only when landing on the explore page and not when navigating into it.
    // https://www.pivotaltracker.com/story/show/162820413
    if (!process.browser && tours && getTourToReschedule({ userTours: tours })) {
      return <Redirect to='/tour' />;
    }

    const showNoResults = (
      (listingsIsFullyLoaded && !listings.length)
      || listingsViewMoreIndex === 0
    );

    let resultsCount = null;

    if (typeof listingsViewMoreIndex === 'number' && listingsViewMoreIndex > 0) {
      resultsCount = listingsViewMoreIndex;
    }
    else if (listingsTotal > 0) {
      resultsCount = listingsTotal;
    }

    const renterProfile = renterProfilesByUserId && renterProfilesByUserId[currentUser.id] || null;

    return (
      <div className='page-explore'>
        {selectedSuggestedSearchFilter &&
          <SrSuggestedSearchRemove
            displayName={selectedSuggestedSearchFilter.displayName}
            removeSuggestedFilter={this._removeSuggestedFilter}
          />
        }
        <div className='listView'>
          <div className='tipsVariation'>
            <Navbar
              leftButton={(
                <NavbarButton
                  onClick={() => history.push('/search')}
                >
                  Filter
                </NavbarButton>
              )}
              rightButton={(
                <NavbarButton
                  onClick={() => history.push('/explore/map')}
                >
                  Map
                </NavbarButton>
              )}
            />
          </div>
          {!hasRoleAnonymous(currentUser.roles) && (
            <SrContinueApplicationButton />
          )}
          {!hasRoleAnonymous(currentUser.roles) && (
            <TabBar />
          )}
          {showNoResults && (
            <NoResults
              householdApplications={householdApplications}
              bookmarksByListingId={bookmarksByListingId}
              onBookmarkClick={this._onBookmarkClick}
              currentUser={currentUser}
              photoResolution={listingPhotoResolution}
              renterProfile={renterProfile}
            />
          )}
          <div>
            {resultsCount && (
              <SrResultsMatchedCard
                resultsCount={resultsCount}
                totalListingsCount={totalListingsCount}
              />
            )}
            <ListingsListView
              savePositionWithId={SCREEN_ID_LISTINGS}
              listings={listings}
              isLoading={listingsApiState.inProgress}
              onLoadMore={this._onLoadMore}
              householdApplications={householdApplications}
              bookmarksByListingId={bookmarksByListingId || {}}
              onBookmarkClick={this._onBookmarkClick}
              showEducationTourCard={!educationTourCardClosed && !showNoResults}
              listingsViewMoreIndex={listingsViewMoreIndex}
              listingsViewMoreCount={listingsViewMoreCount}
              listingsIsFullyLoaded={listingsIsFullyLoaded}
              listingsTotal={listingsTotal}
              enableAllListings={this._enableAllListings}
              currentUser={currentUser}
              photoResolution={listingPhotoResolution}
              renterProfile={renterProfile}
              requestedFrom={requestedFrom}
            />
          </div>
          {listingsIsFullyLoaded
            && suggestedSearchFilters
            && (
              <SrSuggestedSearchesSection
                suggestedSearchFilters={suggestedSearchFilters}
                dispatch={dispatch}
              />
            )}
        </div>
      </div>
    );
  }
}

ListingsPage.displayName = 'ListingsPage';

export default ListingsPage;
